import React, { useEffect } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Snackbar,
} from "@mui/material";
import TrophyIcon from "@mui/icons-material/EmojiEvents";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import { DashboardPageProps } from "../../Interfaces/ComponentProps";
import { useGetLeaderboardQuery } from "../../Apis/progressApi";
import { useSelector } from "react-redux";
import { RootState } from "../../Storage/Redux/store";
import { userModel } from "../../Interfaces";

const DashboardPage: React.FC<DashboardPageProps> = ({
  results,
}) => {
  const { data: leaderboardData, isLoading, error, refetch } =
    useGetLeaderboardQuery();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const userData: userModel = useSelector(
    (state: RootState) => state.userAuthStore
  );

  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: "",
    severity: "info" as "success" | "error" | "info" | "warning",
  });

  useEffect(() => {
    if (error) {
      setSnackbar({
        open: true,
        message: "Failed to load leaderboard data.",
        severity: "error",
      });
    }
  }, [error]);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        <CircularProgress color="primary" />
        <Typography>Loading leaderboard...</Typography>
      </Box>
    );
  }

  const topTen = leaderboardData?.topTen || [];
  const currentUserRank = leaderboardData?.currentUserRank || 0;
  const totalParticipants = leaderboardData?.totalParticipants || 0;

  const getRankBadge = (rank: number): string => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };

  const getScoreColor = (score: number): string => {
    if (score >= 90) return "success.main";
    if (score >= 70) return "warning.main";
    return "error.main";
  };

  const isCurrentUser = (name: string): boolean => name === userData.email;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: "rgba(81, 35, 132, 1)", fontWeight: 600 }}
        >
          Contest Scoreboard
        </Typography>
        <Button
          variant="outlined"
          color="error"
          startIcon={<CloseIcon />}
          onClick={() => window.location.reload()}
        >
          Close
        </Button>
      </Box>

      {/* Current User Stats */}
      {currentUserRank > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography>
            <strong>Your Ranking:</strong> {getRankBadge(currentUserRank)} out of{" "}
            {totalParticipants} participants
          </Typography>
          <Typography>
            <strong>Your Total Score:</strong> {results?.finalScore || 0} points
          </Typography>
        </Alert>
      )}

      {/* Leaderboard Card */}
      <Card sx={{ borderRadius: 3, boxShadow: 4 }}>
        <CardHeader
          title={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TrophyIcon sx={{ mr: 1, color: "#FFD700" }} />
              <Typography
                variant="h6"
                sx={{ color: "white", fontWeight: "bold" }}
              >
                Top 10 Leaderboard
              </Typography>
            </Box>
          }
          sx={{ backgroundColor: "rgba(81, 35, 132, 1)" }}
        />
        <CardContent  sx={{ p: 0 ,overflow:"auto"}}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: "bold" }}>No.</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Full Name</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Total Scores</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Quiz Taken</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topTen.map((participant) => {
                const isUserRow = isCurrentUser(participant.username);
                return (
                  <TableRow
                    key={participant.userId}
                    hover
                    sx={{
                      backgroundColor: isUserRow
                        ? "rgba(255, 224, 130, 0.3)"
                        : "inherit",
                      "&:hover": {
                        backgroundColor: "rgba(81, 35, 132, 0.05)",
                      },
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="bold">
                        {getRankBadge(participant.rank)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        sx={{
                          color: isUserRow ? "primary.main" : "text.primary",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {participant.fullName}
                        {isUserRow && <PersonIcon sx={{ ml: 1 }} />}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        fontWeight="bold"
                        sx={{ color: getScoreColor(participant.totalScore) }}
                      >
                        {participant.totalScore}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={participant.quizzesTaken}
                        color="info"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
};

export default DashboardPage;
